import React, { createContext, useContext, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Suspense from '@atlaskit/react-ufo/suspense';

import { AgentRowAvatar } from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import { useAgentAvatarColorMode } from '@confluence/agent-user-dual-avatar/entry-points/useAgentAvatarColorMode.ts';
import { getAgentBrand } from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { UserShape } from '../ownership-models.ts';

import CollaboratorsQuery, {
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data,
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery as CollaboratorsQueryType,
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery$variables,
} from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';
import { getCollaboratorItems } from './getCollaboratorItems.ts';
import { getContentCollaboratorsQueryVariables } from './getContentCollaboratorsQueryVariables.ts';
import { getUserAvatarSrc } from './getUserAvatarSrc.ts';

const MAX_NUMBERED_OTHER_CONTRIBUTORS = 4;
const MAX_TOTAL_BYLINE_AVATARS = 3;

const messages = defineMessages({
	byline: {
		id: 'content-ownership.byline.owner-with-collaborators',
		defaultMessage:
			'{ownerName}{otherCount, plural, =0 {} one { and 1 other} other { and # others}}',
		description:
			'Page byline showing the owner followed by the number of other human and agent-person contributors.',
	},
	bylineWithoutCount: {
		id: 'content-ownership.byline.owner-with-others',
		defaultMessage: '{ownerName} and others',
		description: 'Page byline showing the owner when there are more than four other contributors.',
	},
});

const styles = cssMap({
	avatarStack: {
		isolation: 'isolate',
	},
	avatar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		flexShrink: 0,
	},
	overlappingAvatar: {
		marginInlineStart: token('space.negative.050'),
	},
});

type CollaboratorsBylineProps = {
	owner: UserShape;
	fallbackContributorCount: number;
};

// Error boundaries retain their fallback component, so read the current owner/count through context.
const BylineFallbackContext = createContext<React.JSX.Element | null>(null);
const BylineFallback = () => useContext(BylineFallbackContext);

// Mounted only for the agent-presence experiment. The preloader warms the same Relay operation.
export const CollaboratorsByline = (props: CollaboratorsBylineProps): React.JSX.Element => {
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const fallback = useMemo(
		() => (
			<CollaboratorsBylineContent
				owner={props.owner}
				fallbackContributorCount={props.fallbackContributorCount}
			/>
		),
		[props.owner, props.fallbackContributorCount],
	);

	if (!contentId || !spaceKey || !cloudId) {
		return fallback;
	}

	const variables = getContentCollaboratorsQueryVariables({
		cloudId,
		contentId,
		spaceKey,
		shouldIncludeJobTitle: expValEquals('cc_show_byline_job_title', 'isEnabled', true),
	});

	return (
		<BylineFallbackContext.Provider value={fallback}>
			<GenericErrorBoundary
				key={`${cloudId}:${contentId}:${spaceKey}`}
				attribution={Attribution.PAGE_EXPERIENCES}
				renderOnError={BylineFallback}
			>
				<Suspense fallback={fallback} interactionName="collaborators-byline">
					<QueriedCollaboratorsByline {...props} variables={variables} />
				</Suspense>
			</GenericErrorBoundary>
		</BylineFallbackContext.Provider>
	);
};

const QueriedCollaboratorsByline = ({
	variables,
	...props
}: CollaboratorsBylineProps & {
	variables: PageOwnershipByLineWithCollaboratorsDropdownContentQuery$variables;
}): React.JSX.Element => {
	const data = useLazyLoadQuery<CollaboratorsQueryType>(CollaboratorsQuery, variables, {
		fetchPolicy: 'store-or-network',
	});

	return (
		<CollaboratorsBylineContent {...props} collaborators={data.confluence_contentCollaborators} />
	);
};

const CollaboratorsBylineContent = ({
	owner,
	fallbackContributorCount,
	collaborators,
}: CollaboratorsBylineProps & {
	collaborators?: PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data['confluence_contentCollaborators'];
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const colorMode = useAgentAvatarColorMode();
	const items = useMemo(
		() => getCollaboratorItems(collaborators, owner.userId),
		[collaborators, owner.userId],
	);
	const uniqueItems = useMemo(() => {
		const seen = new Set<string>();
		return items.filter((item) => {
			const identity =
				item.kind === 'agent'
					? JSON.stringify(['agent', item.agent.accountId])
					: JSON.stringify(['human', item.user.userId || item.key]);
			if (seen.has(identity)) {
				return false;
			}
			seen.add(identity);
			return true;
		});
	}, [items]);
	// Count each agent-person pair in the text; deduplicate identities only in the avatar stack.
	const otherCount = collaborators?.nodes ? items.length : fallbackContributorCount;
	// The owner is rendered separately and occupies the first slot in the avatar stack.
	const visibleNonOwnerItems = uniqueItems.slice(0, MAX_TOTAL_BYLINE_AVATARS - 1);

	return (
		<Inline space="space.050" alignBlock="center">
			{/* The button's text names the collection; individual avatars are decorative. */}
			<Inline
				alignBlock="center"
				aria-hidden="true"
				testId="byline-collaborator-avatars"
				xcss={styles.avatarStack}
			>
				{/* Paint earlier avatars above later ones, matching AvatarGroup's stack order. */}
				<Box xcss={styles.avatar} style={{ zIndex: visibleNonOwnerItems.length + 1 }}>
					<Avatar
						src={getUserAvatarSrc(owner)}
						borderColor={token('elevation.surface')}
						size="xxsmall"
					/>
				</Box>
				{visibleNonOwnerItems.map((item, index) => (
					<Box
						key={item.key}
						xcss={cx(styles.avatar, styles.overlappingAvatar)}
						style={{ zIndex: visibleNonOwnerItems.length - index }}
					>
						{item.kind === 'agent' ? (
							<AgentRowAvatar
								name={item.agent.displayName}
								agentIcon={getAgentBrand(item.agent.displayName)?.agentIcon}
								avatarUrl={item.agent.avatarUrl}
								avatarSize="xxsmall"
								hasInlineSpacing={false}
								colorMode={colorMode}
							/>
						) : (
							<Avatar
								src={getUserAvatarSrc(item.user)}
								borderColor={token('elevation.surface')}
								size="xxsmall"
							/>
						)}
					</Box>
				))}
			</Inline>
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{formatMessage(
					otherCount > MAX_NUMBERED_OTHER_CONTRIBUTORS
						? messages.bylineWithoutCount
						: messages.byline,
					{ ownerName: owner.fullName, otherCount },
				)}
			</Text>
		</Inline>
	);
};
